import {Injectable, NgZone, signal} from '@angular/core';
import {
  ConfirmVerificationCodeOptions,
  FirebaseAuthentication,
  GetIdTokenOptions,
  PhoneVerificationCompletedEvent,
  SignInResult,
  SignInWithPhoneNumberOptions,
  User,
} from '@capacitor-firebase/authentication';
import {Capacitor} from '@capacitor/core';
import {Platform} from '@ionic/angular';
import {BehaviorSubject, firstValueFrom, Observable, ReplaySubject, Subject} from 'rxjs';
import {take} from 'rxjs/operators';
import {Router} from "@angular/router";
import {StorageService} from "@core/services/storage.service";
import {toObservable} from "@angular/core/rxjs-interop";

@Injectable({
  providedIn: 'root',
})
export class PhoneNumberLoginService {
  public userSignal = signal<User | null>(null);
  public user$ = this.userSignal();

  private currentUserSubject = new BehaviorSubject<User | null>(undefined);
  private phoneVerificationCompletedSubject =
    new Subject<PhoneVerificationCompletedEvent>();
  private phoneCodeSentSubject = new Subject<{
    verificationId: string;
  }>();

  constructor(
    private readonly platform: Platform,
    private readonly ngZone: NgZone,
    private router: Router,
    private storageService: StorageService,
  ) {
    FirebaseAuthentication.removeAllListeners().then(async () => {
      await FirebaseAuthentication.addListener('authStateChange', async (change: any) => {
        await this.ngZone.run(async () => {
          console.log('change', change);
          // this.currentUserSubject.next(change.user);
          // this.storageService.set('user', change.user);
          await this.setAuthUser(change.user);
        });
      });
      await FirebaseAuthentication.addListener(
        'phoneVerificationCompleted',
        async (event: any) => {
          this.ngZone.run(() => {
            this.phoneVerificationCompletedSubject.next(event);
          });
        },
      );
      await FirebaseAuthentication.addListener('phoneCodeSent', async (event: any) => {
        this.ngZone.run(() => {
          this.phoneCodeSentSubject.next(event);
        });
      });
    });
    // Only needed to support dev livereload.
    FirebaseAuthentication.getCurrentUser().then(async (result: any) => {
      await this.ngZone.run(async () => {
        this.currentUserSubject.next(result.user);
        await this.setAuthUser(result.user);
      });
    });
  }

  public get currentUser$(): Observable<User | null> {
    return this.currentUserSubject.asObservable();
  }

  public get phoneVerificationCompleted$(): Observable<PhoneVerificationCompletedEvent> {
    return this.phoneVerificationCompletedSubject.asObservable();
  }

  public get phoneCodeSent$(): Observable<{
    verificationId: string;
  }> {
    return this.phoneCodeSentSubject.asObservable();
  }

  public async getRedirectResult(): Promise<SignInResult | undefined> {
    if (Capacitor.isNativePlatform()) {
      return;
    }
    return FirebaseAuthentication.getRedirectResult();
  }

  public confirmVerificationCode(
    options: ConfirmVerificationCodeOptions,
  ): Promise<SignInResult> {
    return FirebaseAuthentication.confirmVerificationCode(options);
  }

  public getCurrentUser(): Promise<User | null> {
    return firstValueFrom(this.currentUser$.pipe(take(1)));
  }

  public async getIdToken(options?: GetIdTokenOptions): Promise<string> {
    const result = await FirebaseAuthentication.getIdToken(options);
    return result.token;
  }

  public signInWithPhoneNumber(
    options: SignInWithPhoneNumberOptions,
  ): Promise<void> {
    return FirebaseAuthentication.signInWithPhoneNumber(options);
  }

  async setAuthUser(user: User | null) {
    this.currentUserSubject.next(user);
    this.userSignal.set(user);
    // Only persist a valid user. Firebase emits a transient `null` during cold
    // start / app resume / token refresh before the session is restored; writing
    // that null here would wipe the stored session and bounce the user to login.
    // The stored 'user' is cleared explicitly on logout (AuthHttpService.logout).
    if (user) {
      await this.storageService.set('user', user);
    }
  }
}
